import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatErr } from "../../api";
import { downloadFile } from "../../download";
import { SectionTitle, StatusPill, EmptyState, ProgressBar, stripHtml } from "../../components/Atoms";
import { Stack, Calendar, ArrowRight, Users, ListChecks, Megaphone, FileXls, FileCsv } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function ClientProjectsPage() {
  const [list, setList] = useState([]);
  const nav = useNavigate();

  useEffect(() => { (async () => { const { data } = await api.get("/projects"); setList(data); })(); }, []);

  const dlCsv = async (pid, title) => {
    try { await downloadFile(`/reports/project/${pid}/csv`, `project-${title}.csv`); toast.success("CSV downloaded"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-5">
      <SectionTitle title="My Projects" subtitle="Your active and historic campaigns" />
      {list.length === 0 ? <EmptyState icon={Stack} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(p => {
            const taskCount = p.task_count || 0;
            const subCount = p.submission_count || 0;
            const ambCount = p.ambassador_count || 0;
            const progress = taskCount * Math.max(1, ambCount);
            const pct = progress > 0 ? Math.min(100, Math.round((subCount / progress) * 100)) : 0;
            return (
              <div key={p.id} onClick={(e) => { if (e.target.closest('button')) return; nav(`/client/projects/${p.id}`); }} className="glass p-5 hover-lift cursor-pointer" data-testid={`client-project-${p.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="text-white font-bold truncate">{p.title}</div>
                    <div className="text-xs text-muted truncate">{p.client_name || p.category}</div>
                  </div>
                  <StatusPill status={p.status} />
                </div>
                <div className="text-sm text-muted line-clamp-2 mb-3">{stripHtml(p.description)}</div>
                <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                  <Box icon={ListChecks} label="Tasks"        value={taskCount} />
                  <Box icon={Users}      label="Creators"     value={ambCount} />
                  <Box icon={Megaphone}  label="Submissions"  value={subCount} />
                </div>
                <div className="mb-3">
                  <div className="flex justify-between text-[10px] text-muted mb-1">
                    <span>Project progress</span><span>{pct}%</span>
                  </div>
                  <ProgressBar value={pct} max={100} color="#22D3EE" />
                </div>
                <div className="text-[10px] text-muted flex items-center gap-1"><Calendar size={12} /> {fmtDate(p.start_date)} → {fmtDate(p.end_date)}</div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => nav(`/client/projects/${p.id}`)} className="btn-primary flex-1 flex items-center justify-center gap-1.5 text-sm" data-testid={`open-project-${p.id}`}>
                    Open project <ArrowRight size={14} weight="bold" />
                  </button>
                  <button onClick={() => dlCsv(p.id, p.title)} className="btn-ghost text-sm flex items-center gap-1.5" data-testid={`csv-project-${p.id}`} title="Download CSV">
                    <FileCsv size={14} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Box = ({ icon: Icon, label, value }) => (
  <div className="bg-white/5 rounded-xl px-2 py-2">
    <div className="flex items-center gap-1 text-muted"><Icon size={12} weight="bold" /> <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span></div>
    <div className="text-white font-bold mt-0.5">{value}</div>
  </div>
);
